import React from "react";
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
  Wrap,
  WrapItem,
  Link,
} from "@chakra-ui/react";
import { FaStar } from "react-icons/fa";
import DashboardMockup from "./DashboardMockup";
import {
  FROST,
  GhostBtn,
  MotionBox,
  Pill,
  PrimaryBtn,
  TEAL,
  TEAL_DARK,
  TEAL_LIGHT,
} from "./utils";

interface HeroProps {
  headingLevel?: "h1" | "h2";
}

const Hero: React.FC<HeroProps> = ({ headingLevel = "h1" }) => {
  return (
    <Box
      as="section"
      pt={{ base: 24, md: 28 }}
      pb={{ base: 16, md: 20 }}
      px={{ base: 5, md: 8 }}
      bgGradient={`linear(165deg, ${FROST} 0%, white 55%)`}
      overflow="hidden"
    >
      <Container maxW="6xl">
        <Grid
          templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
          gap={{ base: 12, lg: 16 }}
          alignItems="center"
        >
          <GridItem>
            <MotionBox
              initial={{ opacity: 0, x: -32 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.65 } as any}
            >
              <VStack align="start" gap={5}>
                <HStack gap={2}>
                  <Box
                    w={2}
                    h={2}
                    borderRadius="full"
                    bg={TEAL}
                    sx={{ animation: "pulse 2s infinite" }}
                  />
                  <Pill>Ingyenes próbaidőszak · Kártyaadatok nélkül</Pill>
                </HStack>

                <Heading
                  as={headingLevel}
                  fontSize={{ base: "2.4rem", md: "3rem", lg: "3.4rem" }}
                  fontWeight={800}
                  color={TEAL_DARK}
                  letterSpacing="-0.03em"
                  lineHeight={1.12}
                >
                  Hagyd, hogy az ügyfelek{" "}
                  <Box as="span" color={TEAL}>
                    maguk foglaljanak.
                  </Box>
                </Heading>

                <Text
                  fontSize="lg"
                  color="gray.500"
                  lineHeight={1.7}
                  maxW="440px"
                  fontWeight={300}
                >
                  A Wobble egyszerű foglalóoldalt ad, amit megoszthatsz
                  ügyfeleidnek. Nincs több üzenetváltás, nincs elfelejtett
                  időpont – csak egy naptár, ami magától megtelik.
                </Text>
                <Text
                  fontSize="lg"
                  color="gray.500"
                  lineHeight={1.7}
                  maxW="440px"
                  fontWeight={500}
                >
                  Időpontfoglaló rendszer havidíjas rendszerben már 2999 ft/ hó
                  áron.{" "}
                  <Link
                    href="https://wobble.hu/csomagok"
                    color="blue.500"
                    fontWeight={700}
                    _hover={{ textDecoration: "underline" }}
                  >
                    Árak és részletek
                  </Link>{" "}
                  itt olvashatók.
                </Text>

                <Wrap spacing={3} pt={2}>
                  <WrapItem>
                    <PrimaryBtn href="https://book.wobble.hu">
                      Ingyenes konzultáció
                    </PrimaryBtn>
                  </WrapItem>
                  <WrapItem>
                    <GhostBtn href="https://app.wobble.hu/signup_page">
                      Kipróbálom ingyen
                    </GhostBtn>
                  </WrapItem>
                </Wrap>

                {/* social proof strip */}
                <HStack gap={3} pt={1}>
                  <HStack gap={0}>
                    {["KE", "NP", "SzA", "HB"].map((ini, i) => (
                      <Box
                        key={i}
                        w={8}
                        h={8}
                        borderRadius="full"
                        bg={["#e8f4f7", "#fef3e8", "#eef7ee", "#f3eef7"][i]}
                        border="2px solid white"
                        ml={i ? "-10px" : 0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="9px"
                        fontWeight={700}
                        color={TEAL_DARK}
                      >
                        {ini}
                      </Box>
                    ))}
                  </HStack>
                  <Box>
                    <HStack gap={0.5} mb="2px">
                      {[...Array(5)].map((_, i) => (
                        <Icon
                          key={i}
                          as={FaStar}
                          color={TEAL_LIGHT}
                          boxSize={3}
                        />
                      ))}
                    </HStack>
                    <Text fontSize="xs" color="gray.400">
                      Szolgáltatóknak
                    </Text>
                  </Box>
                </HStack>
              </VStack>
            </MotionBox>
          </GridItem>

          {/* mockup */}
          <GridItem>
            <MotionBox
              initial={{ opacity: 0, scale: 0.88 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.65, delay: 0.15 } as any}
            >
              <DashboardMockup />
            </MotionBox>
          </GridItem>
        </Grid>
      </Container>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
      `}</style>
    </Box>
  );
};

export default Hero;
